import React, { useState } from 'react';
import { Modal, notification, Space } from 'antd';
import SpinLoad from '../../../../components/SpinLoad';
import INTERNAL_LINKS from '../../../../enums/InternalLinks';
import Paragraph from 'antd/es/typography/Paragraph';

import { useHistory, useLocation } from 'react-router-dom';
import AjaxTable from '../../../../components/AjaxTable';
import {
  ajaxTable as filterAjaxTable,
  massivePreselect,
  massiveReject,
} from '../../../../api/module/announcement_preselection';
import { CustomButton, IconButton } from '../../../../components';
import { EyeOutlined, ScheduleFilled } from '@ant-design/icons';
import { catchExceptionSRate } from '../../../../utils/helpers/helpers';
import { get as getDocumentUrl } from '../../../../api/module/document_repository';
import PdfViewer from '../../../../components/PdfViewer';
import MESSAGES from '../../../../enums/Messages';

const PorRevisar = ({
  announcement_id,
  announcement_rol_id,
  onRefresh,
  advancedSearch,
}) => {
  const navigate = useHistory();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [loadingPDF, setLoadingPDF] = useState(undefined);
  const [pdfURL, setPdfUrl] = useState(undefined);
  const [reloadSource, setReloadSource] = useState(false);
  const [loadingMassive, setLoadingMassive] = useState(undefined);
  const [selection, setSelection] = useState([]);

  const getPDFUrl = async (docID) => {
    try {
      setLoadingPDF(docID);
      const { data: responseData } = await getDocumentUrl(`${docID}.pdf`);
      setPdfUrl(responseData.url);
      setLoadingPDF(null);
    } catch (err) {
      catchExceptionSRate(err);
      setLoadingPDF(null);
    }
  };

  const columns = [
    {
      title: 'Nombres y Apellidos',
      dataIndex: 'nombres_apellidos',
    },
    {
      title: 'Doc Identificación',
      dataIndex: 'identificacion',
    },
    {
      title: 'País dom',
      dataIndex: 'pais_dom',
    },
    {
      title: 'Depto dom',
      dataIndex: 'depto_dom',
    },
    {
      title: 'Ciudad dom',
      dataIndex: 'ciudad_dom',
    },
    {
      title: 'Lugar que aplicó',
      dataIndex: 'lugar',
      render: (t) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {(t || '').split(',;,').map((role, idx) => (
            <span key={idx}>{role.trim()}</span>
          ))}
        </div>
      ),
    },
    {
      title: 'HV cargada',
      key: 'hv',
      align: 'center',
      render: (_, record) =>
        record.tipo_persona === 1 ? (
          record.tercero_hv ? (
            <IconButton
              title="Ver PDF"
              bordered
              color="blue"
              onClick={async () => {
                await getPDFUrl(record.tercero_hv);
              }}
              icon={<EyeOutlined />}
              loading={loadingPDF === record.tercero_hv}
              disabled={loadingPDF}
            />
          ) : (
            'No cargado'
          )
        ) : null,
    },
    {
      title: 'Pregrado',
      dataIndex: 'pregrado',
      render: (t) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {(t || '').split(',;,').map((role, idx) => (
            <span key={idx}>{role.trim()}</span>
          ))}
        </div>
      ),
    },
    {
      title: 'Documentos',
      key: 'documentos',
      align: 'center',
      render: (_, record) => (
        <IconButton
          title="Validar"
          bordered
          color="bigaro"
          onClick={() => {
            const formPath = {
              pathname: `${INTERNAL_LINKS.ANNOUNCEMENT_PRESELECTION_VALIDATION_FORM}/${announcement_id}/${record.id}/${announcement_rol_id}`,
              state: { fromSearch: location.search },
            };

            if (record.cargo_aplicado)
              Modal.error({
                okText: 'Entendido',
                title: 'Atención',
                onOk: () => {
                  navigate.push(formPath);
                },
                content: (
                  <Paragraph>{`El usuario también se encuentra aplicando al cargo ${record.cargo_aplicado} de esta convocatoria.
                      ¿Desea continuar con el proceso para este cargo?. En caso que no continúe, por favor rechazarlo.`}</Paragraph>
                ),
              });
            else navigate.push(formPath);
          }}
          icon={<ScheduleFilled />}
        />
      ),
    },
  ];

  const rowSelection = {
    fixed: true,
    preserveSelectedRowKeys: true,
    selectedRowKeys: selection,
    onChange: (selectedRowKeys, selectedRows) => {
      setSelection(selectedRowKeys);
    },
  };

  const onMassivePreselect = async (skipWarning = false) => {
    if (!skipWarning) {
      Modal.confirm({
        title: 'Preselección',
        content: `Esto aplicará para ${selection.length} registros ¿Está seguro(a) de esta acción?`,
        okText: 'Si',
        cancelText: 'No',
        onOk: () => onMassivePreselect(true),
      });
      return;
    }
    try {
      setIsLoading(true);
      setLoadingMassive('preselect');
      const { data: resData } = await massivePreselect(
        { convocatoria_rol: announcement_rol_id },
        { selection: selection },
      );
      if (resData.warning)
        Modal.warning({
          title: 'Aviso',
          content:
            'Hay registro(s) que ya tiene(n) estado en algún modulo. No fue posible calificarlo(s) masivamente. Ubíquelo(s) en la pestaña "Por revisar"',
        });
      notification.success({
        message: MESSAGES.SUCCESS_MSG,
        description: MESSAGES.DESC_SUCCESS_MSG,
        duration: 2,
      });
      setReloadSource({});
      setSelection([]);
    } catch (error) {
      catchExceptionSRate(error);
    } finally {
      setIsLoading(false);
      setLoadingMassive(undefined);
    }
  };

  const onMassiveReject = async (reject_type, skipWarning = false) => {
    if (!skipWarning) {
      Modal.confirm({
        title: `Rechazo por ${
          reject_type === 'reject_place'
            ? 'lugar'
            : reject_type === 'reject_experience'
              ? 'experiencia'
              : reject_type === 'reject_training'
                ? 'formación'
                : 'convocatoria anulada'
        }`,
        content: `Esto aplicará para ${selection.length} registros ¿Está seguro(a) de esta acción?`,
        okText: 'Si',
        cancelText: 'No',
        onOk: () => onMassiveReject(reject_type, true),
      });
      return;
    }
    try {
      setIsLoading(true);
      setLoadingMassive(reject_type);
      const { data: resData } = await massiveReject(
        { convocatoria_rol: announcement_rol_id },
        { selection: selection, mass_type: reject_type },
      );
      if (resData.warning)
        Modal.warning({
          title: 'Aviso',
          content:
            'Hay registro(s) que ya tiene(n) estado en algún modulo. No fue posible calificarlo(s) masivamente. Ubíquelo(s) en la pestaña "Por revisar"',
        });
      notification.success({
        message: MESSAGES.SUCCESS_MSG,
        description: MESSAGES.DESC_SUCCESS_MSG,
        duration: 2,
      });
      setReloadSource({});
      setSelection([]);
    } catch (error) {
      catchExceptionSRate(error);
    } finally {
      setIsLoading(false);
      setLoadingMassive(undefined);
    }
  };

  return (
    <SpinLoad loading={isLoading}>
      <Space>
        <CustomButton
          htmlType="button"
          type="default"
          text="Preseleccionados"
          onClick={() => onMassivePreselect()}
          loading={loadingMassive === 'preselect'}
          disabled={loadingMassive || selection.length < 2}
        />
        <CustomButton
          htmlType="button"
          type="danger"
          text="Rechazo por lugar"
          onClick={() => onMassiveReject('reject_place')}
          loading={loadingMassive === 'reject_place'}
          disabled={loadingMassive || selection.length < 2}
        />
        <CustomButton
          htmlType="button"
          type="danger"
          text="Rechazo por experiencia"
          onClick={() => onMassiveReject('reject_experience')}
          loading={loadingMassive === 'reject_experience'}
          disabled={loadingMassive || selection.length < 2}
        />
        <CustomButton
          htmlType="button"
          type="danger"
          text="Rechazo por formación"
          onClick={() => onMassiveReject('reject_training')}
          loading={loadingMassive === 'reject_training'}
          disabled={loadingMassive || selection.length < 2}
        />
        <CustomButton
          htmlType="button"
          type="danger"
          text="Convocatoria anulada"
          onClick={() => onMassiveReject('reject_convocatoria')}
          loading={loadingMassive === 'reject_convocatoria'}
          disabled={loadingMassive || selection.length < 2}
        />
      </Space>
      <AjaxTable
        columns={columns}
        pageSize={10}
        tableTitle={'Listado de contratistas por revisar'}
        endpoint={filterAjaxTable}
        searchInput
        exportCSV
        reloadButton
        query={{
          convocatoria_rol: announcement_rol_id,
          estado_tercero: 10,
          advanced_search: advancedSearch,
        }}
        reloadSource={reloadSource}
        rowSelection={rowSelection}
        setCount={() => onRefresh()}
        showSizeChanger
      />
      <PdfViewer pdf={pdfURL} onCancel={() => setPdfUrl()} open={!!pdfURL} />
    </SpinLoad>
  );
};

export default PorRevisar;
